import type { CSSProperties } from 'react';
import { NEIS_SHORT_EDGE_HEX } from './neisShortEdgeColor';
import type { WeeklyGuideSlotSurface } from './weeklyLearningGuideSurface';

export type NeisVsBaseKind = 'match-base' | 'diff-track' | 'dedicated-mismatch';

export const NEIS_UNREGISTERED_TIMETABLE_CLASS = 'cp-weekly-learning-guide-cell--neis-unregistered-timetable';

const NEIS_SHORT_EDGE_PX = '2px';

export function resolveWeeklyGuideNeisCellText(cell: {
  slotSurface: WeeklyGuideSlotSurface;
  subject: string;
  neisTrackName?: string;
  neisUnregisteredTimetableText?: string;
  neisDedicated?: boolean;
}): { text: string; unregistered: boolean; showDedicatedLabel: boolean } {
  if (cell.slotSurface === 'holiday') {
    return { text: cell.subject, unregistered: false, showDedicatedLabel: false };
  }
  const track = (cell.neisTrackName ?? '').trim();
  if (track) {
    return {
      text: track,
      unregistered: false,
      showDedicatedLabel: !!cell.neisDedicated,
    };
  }
  const raw = (cell.neisUnregisteredTimetableText ?? '').trim();
  if (raw) return { text: raw, unregistered: true, showDedicatedLabel: false };
  return { text: '', unregistered: false, showDedicatedLabel: false };
}

export function neisVsBaseBackgroundClass(
  neisVsBase: NeisVsBaseKind | undefined,
  slotSurface: WeeklyGuideSlotSurface
): string {
  if (slotSurface !== 'class') return '';
  if (neisVsBase === 'match-base') return 'cp-weekly-learning-guide-cell--neis-match-base';
  if (neisVsBase === 'diff-track') return 'cp-weekly-learning-guide-cell--neis-diff-track';
  if (neisVsBase === 'dedicated-mismatch') return 'cp-weekly-learning-guide-cell--neis-dedicated-mismatch';
  return '';
}

/** 증배·단축 하이라이트: 이웃 셀도 하이라이트면 공유 변은 생략해 겹선이 나지 않게 함 */
export function buildNeisShortIncreaseEdgeStyle(
  opts: {
    self: boolean;
    topNeighbor: boolean;
    bottomNeighbor: boolean;
    leftNeighbor: boolean;
    rightNeighbor: boolean;
  },
  edgeColor: string = NEIS_SHORT_EDGE_HEX.light,
  /** html2canvas는 inset box-shadow를 배경 채움으로 잘못 그려 PDF에서 셀이 초록으로 번짐 */
  mode: 'shadow' | 'border' = 'shadow'
): CSSProperties | undefined {
  if (!opts.self) return undefined;
  const w = NEIS_SHORT_EDGE_PX;

  if (mode === 'border') {
    const style: CSSProperties = { boxShadow: 'none' };
    if (!opts.topNeighbor) style.borderTop = `${w} solid ${edgeColor}`;
    if (!opts.bottomNeighbor) style.borderBottom = `${w} solid ${edgeColor}`;
    if (!opts.leftNeighbor) style.borderLeft = `${w} solid ${edgeColor}`;
    if (!opts.rightNeighbor) style.borderRight = `${w} solid ${edgeColor}`;
    const hasEdge =
      !opts.topNeighbor || !opts.bottomNeighbor || !opts.leftNeighbor || !opts.rightNeighbor;
    return hasEdge ? style : { boxShadow: 'none' };
  }

  const parts: string[] = [];
  if (!opts.topNeighbor) parts.push(`inset 0 ${w} 0 0 ${edgeColor}`);
  if (!opts.bottomNeighbor) parts.push(`inset 0 -${w} 0 0 ${edgeColor}`);
  if (!opts.leftNeighbor) parts.push(`inset ${w} 0 0 0 ${edgeColor}`);
  if (!opts.rightNeighbor) parts.push(`inset -${w} 0 0 0 ${edgeColor}`);
  if (parts.length === 0) return undefined;
  return { boxShadow: parts.join(', ') };
}
